'use client';

import { useTranslations } from 'next-intl';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { useIsMobile } from '@/hooks/useIsMobile';
import GlareSweep from '@/modals/modules/balances/+ShopModal/components/ShopModalContent/components/bundleCards/GlareSweep';
import openModal from '@/modals/utils/openModal';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import DailyBonusGuestOverlay from './components/DailyBonusGuestOverlay';
import DailyBonusInformationDrawer from './components/DailyBonusInformationDrawer';
import DailyBonusTimer from './components/DailyBonusTimer';
import DailyBonusSkeleton from './components/Skeleton';
import { useDailyBonusRewards } from './hooks/useDailyBonusRewards';

const DailyBonusCard = lazy(() => import('./components/DailyBonusCard'));
interface Props {
  classNameCardGrid?: string;
}

const DailyBonus = ({ classNameCardGrid }: Props) => {
  const t = useTranslations('daily-bonus');
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const userSession = useAppSelector(state => state.user.userSession);
  const isGuest = !userSession;
  const { cards, isLoading, showTimer } = useDailyBonusRewards();

  if (!isHydrated) {
    return <DailyBonusSkeleton />;
  }

  if (!isGuest && isLoading) {
    return <DailyBonusSkeleton />;
  }

  const handleOpenModal = () => {
    if (isMobile) {
      setIsOpen(true);
      return;
    }
    openModal('daily-bonus');
  };

  return (
    <Suspense fallback={<DailyBonusSkeleton />}>
      <section className={cn(
        'relative md:-mx-2 3xl:mx-0 lg:mb-4 3xl:mb-0 max-md:mt-4 rounded-2xl p-px',
        isGuest ? 'glass-stroke-border' : 'gold-border-gradient max-md:shadow-[0px_0px_6px_0px_rgba(230,205,116,0.6)]',
      )}
      >
        {
          isGuest && (
            <div
              className="absolute w-[calc(100%-2px)] rounded-[inherit] h-[calc(100%-2px)] max-md:gold-border-gradient max-md:shadow-[0px_0px_6px_1px_rgba(230,205,116,0.6)]"
            />
          )
        }
        <div className={cn('relative size-full overflow-hidden rounded-[inherit] bg-daily-bonus-glass', isGuest && 'bg-c900/85')}>
          {isGuest && <DailyBonusGuestOverlay />}

          <div className={cn('relative z-1 py-2.75 px-2.75 md:px-1.75 md:py-2.75', isGuest && 'p-3 md:px-2 md:py-3 bg-c900/85')}>
            <div className="flex items-center gap-2 md:gap-1 mb-4 md:mb-3 3xl:mb-2">
              <span className="relative inline-flex shrink-0 size-5.5">
                <Icon name="gold-gift" className="absolute inset-0 size-5.5" />
                {!isGuest && <GlareSweep iconName="gold-gift" travelDistance={40} rotateOrigin="11 11" durationSeconds={3} />}
              </span>
              <h3 className="flex-1 text-white leading-5 text-sm font-semibold">{t('daily-reward')}</h3>
              {showTimer && <DailyBonusTimer />}
            </div>

            <div className={cn('grid grid-cols-5 gap-x-2.25 gap-y-2 md:gap-y-2 md:gap-x-1', classNameCardGrid)}>
              {cards.map(({ id, ...cardProps }, index) => (
                <DailyBonusCard key={id} index={index} {...cardProps} />
              ))}
            </div>

            <div className="flex justify-center mt-4 md:mt-3">
              <button
                type="button"
                className="group flex items-center gap-1 font-medium text-[0.75rem] md:text-[0.625rem] leading-4 text-c300 md:font-medium transition-colors duration-300 md:hover:cursor-pointer md:hover:text-white"
                onClick={handleOpenModal}
              >
                {t('rewards-stack-learn-more')}
                <Icon name="info-payment" className="size-3 text-c300 transition-colors duration-300 md:group-hover:text-white" />
              </button>
            </div>
          </div>

          <DailyBonusInformationDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
        </div>
      </section>
    </Suspense>
  );
};

export default DailyBonus;
